// frontend/src/components/jobPlacement/JobPlacementRelatedForms.jsx
import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";
import PlacementAssessmentForm from "./PlacementAssessmentForm";
import PlacementFollowupForm from "./PlacementFollowupForm";
import JobSearchDataSheet from "./JobSearchDataSheet";

const JobPlacementRelatedForms = ({ jobPlacement, consumer, onBack }) => {
  const { tenant_id: tenantId } = useContext(AuthContext);
  const apiBase = getApiBaseURL();

  const [assessments, setAssessments] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [jobSearchContactCount, setJobSearchContactCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // View states
  const [view, setView] = useState("list"); // "list", "assessment-form", "followup-form", "job-search"
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [selectedFollowup, setSelectedFollowup] = useState(null);

  // Load related forms
  const loadRelatedForms = async () => {
    if (!jobPlacement?.job_placement_id || !tenantId) return;

    setLoading(true);
    try {
      const [assessmentRes, followupRes, contactsRes] = await Promise.all([
        secureAxios.get(
          `${apiBase}/api/placement-assessments/job-placement/${jobPlacement.job_placement_id}`,
          { params: { tenant_id: tenantId } }
        ),
        secureAxios.get(
          `${apiBase}/api/placement-followups/job-placement/${jobPlacement.job_placement_id}`,
          { params: { tenant_id: tenantId } }
        ),
        secureAxios
          .get(
            `${apiBase}/api/job-search-contacts/consumer/${jobPlacement.consumer_id}`,
            { params: { tenant_id: tenantId } }
          )
          .catch(() => ({ data: [] })), // Gracefully handle if endpoint doesn't exist yet
      ]);

      setAssessments(assessmentRes.data);
      setFollowups(followupRes.data);
      setJobSearchContactCount(contactsRes.data?.length || 0);
    } catch (err) {
      console.error("Error loading related forms:", err);
      toast.error("Failed to load related forms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRelatedForms();
  }, [jobPlacement?.job_placement_id, tenantId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    // Parse as local date to avoid timezone shift
    const dateOnly = dateStr.split("T")[0];
    const [year, month, day] = dateOnly.split("-");
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  const handleViewAssessmentPDF = async (assessmentId) => {
    try {
      const res = await secureAxios.get(
        `${apiBase}/api/placement-assessments/pdf/${assessmentId}`,
        { params: { tenant_id: tenantId } }
      );
      if (res.data?.pdf_url) {
        window.open(res.data.pdf_url, "_blank");
      }
    } catch (err) {
      toast.error("Failed to load PDF");
    }
  };

  const handleViewFollowupPDF = async (followupId) => {
    try {
      const res = await secureAxios.get(
        `${apiBase}/api/placement-followups/pdf/${followupId}`,
        { params: { tenant_id: tenantId } }
      );
      if (res.data?.pdf_url) {
        window.open(res.data.pdf_url, "_blank");
      }
    } catch (err) {
      toast.error("Failed to load PDF");
    }
  };

  const handleDeleteAssessment = async (assessmentId) => {
    if (!window.confirm("Are you sure you want to delete this assessment?"))
      return;

    try {
      await secureAxios.delete(
        `${apiBase}/api/placement-assessments/${assessmentId}`,
        { params: { tenant_id: tenantId } }
      );
      toast.success("Assessment deleted");
      loadRelatedForms();
    } catch (err) {
      toast.error("Failed to delete assessment");
    }
  };

  const handleDeleteFollowup = async (followupId) => {
    if (!window.confirm("Are you sure you want to delete this follow-up?"))
      return;

    try {
      await secureAxios.delete(
        `${apiBase}/api/placement-followups/${followupId}`,
        { params: { tenant_id: tenantId } }
      );
      toast.success("Follow-up deleted");
      loadRelatedForms();
    } catch (err) {
      toast.error("Failed to delete follow-up");
    }
  };

  // Render assessment form (create/edit)
  if (view === "assessment-form") {
    return (
      <PlacementAssessmentForm
        assessment={selectedAssessment}
        jobPlacement={jobPlacement}
        consumer={consumer}
        onCancel={() => {
          setView("list");
          setSelectedAssessment(null);
        }}
        onSave={() => {
          setView("list");
          setSelectedAssessment(null);
          loadRelatedForms();
        }}
      />
    );
  }

  // Render followup form (create/edit)
  if (view === "followup-form") {
    return (
      <PlacementFollowupForm
        followup={selectedFollowup}
        jobPlacement={jobPlacement}
        consumer={consumer}
        onCancel={() => {
          setView("list");
          setSelectedFollowup(null);
        }}
        onSave={() => {
          setView("list");
          setSelectedFollowup(null);
          loadRelatedForms();
        }}
      />
    );
  }

  // Render Job Search Data Sheet (CRP-33)
  if (view === "job-search") {
    return (
      <JobSearchDataSheet
        consumer={consumer}
        jobPlacement={jobPlacement}
        onBack={() => {
          setView("list");
          loadRelatedForms();
        }}
      />
    );
  }

  // Render list view
  return (
    <div className="container my-4">
    <div className="job-placement-related-forms">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0 h6">
          <i className="bi bi-files me-2"></i>
          Related Forms for CRP-30
        </h5>
        <button className="btn btn-outline-secondary" onClick={onBack}>
          <i className="bi bi-arrow-left me-1"></i>
          Back to Job Placement Plan
        </button>
      </div>

      {/* Consumer/Plan Info */}
      <div className="card mb-4 bg-light">
        <div className="card-body p-2">
          <div className="row g-2 g-md-3">
            <div className="col-md-4">
              <strong>Consumer:</strong>{" "}
              {consumer?.name || jobPlacement?.consumer_name}
            </div>
            <div className="col-md-8">
              <strong>Vocational Goal:</strong>{" "}
              {jobPlacement?.vocational_goal || "-"}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* CRP-31: Placement Assessments */}
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                <span>
                  <i className="bi bi-clipboard-check me-2"></i>
                  CRP-31: Assessments
                </span>
                <button
                  className="btn btn-light btn-sm"
                  onClick={() => {
                    setSelectedAssessment(null);
                    setView("assessment-form");
                  }}
                >
                  <i className="bi bi-plus-circle me-1"></i>
                  New
                </button>
              </div>
              <div className="card-body p-2 p-md-3">
                {assessments.length === 0 ? (
                  <p className="text-muted text-center py-3">
                    No assessments created yet.
                    <br />
                    <button
                      className="btn btn-outline-primary btn-sm mt-2"
                      onClick={() => {
                        setSelectedAssessment(null);
                        setView("assessment-form");
                      }}
                    >
                      Create First Assessment
                    </button>
                  </p>
                ) : (
                  <ul className="list-group list-group-flush">
                    {assessments.map((a) => (
                      <li
                        key={a.assessment_id}
                        className="list-group-item d-flex justify-content-between align-items-center px-0"
                      >
                        <div>
                          <strong>{formatDate(a.assessment_date)}</strong>
                          <br />
                          <small className="text-muted">
                            {a.staff_recommendation
                              ? a.staff_recommendation.substring(0, 30) + "..."
                              : "No recommendation"}
                          </small>
                        </div>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() =>
                              handleViewAssessmentPDF(a.assessment_id)
                            }
                            title="View PDF"
                          >
                            <i className="bi bi-file-pdf"></i>
                          </button>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => {
                              setSelectedAssessment(a);
                              setView("assessment-form");
                            }}
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() =>
                              handleDeleteAssessment(a.assessment_id)
                            }
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* CRP-32: Placement Follow-Ups */}
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                <span>
                  <i className="bi bi-calendar-check me-2"></i>
                  CRP-32: Follow-Ups
                </span>
                <button
                  className="btn btn-light btn-sm"
                  onClick={() => {
                    setSelectedFollowup(null);
                    setView("followup-form");
                  }}
                >
                  <i className="bi bi-plus-circle me-1"></i>
                  New
                </button>
              </div>
              <div className="card-body p-2 p-md-3">
                {followups.length === 0 ? (
                  <p className="text-muted text-center py-3">
                    No follow-ups created yet.
                    <br />
                    <button
                      className="btn btn-outline-success btn-sm mt-2"
                      onClick={() => {
                        setSelectedFollowup(null);
                        setView("followup-form");
                      }}
                    >
                      Create First Follow-Up
                    </button>
                  </p>
                ) : (
                  <ul className="list-group list-group-flush">
                    {followups.map((f) => (
                      <li
                        key={f.followup_id}
                        className="list-group-item d-flex justify-content-between align-items-center px-0"
                      >
                        <div>
                          <strong>{f.employer_name || "No Employer"}</strong>
                          <br />
                          <small className="text-muted">
                            Placed: {formatDate(f.date_placed)}
                          </small>
                        </div>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleViewFollowupPDF(f.followup_id)}
                            title="View PDF"
                          >
                            <i className="bi bi-file-pdf"></i>
                          </button>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => {
                              setSelectedFollowup(f);
                              setView("followup-form");
                            }}
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDeleteFollowup(f.followup_id)}
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* CRP-33: Job Search Data Sheet */}
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
                <span>
                  <i className="bi bi-search me-2"></i>
                  CRP-33: Job Search
                </span>
                <button
                  className="btn btn-light btn-sm"
                  onClick={() => setView("job-search")}
                >
                  <i className="bi bi-box-arrow-up-right me-1"></i>
                  Open
                </button>
              </div>
              <div className="card-body text-center py-4">
                <i
                  className="bi bi-telephone-outbound text-info"
                  style={{ fontSize: "2.5rem" }}
                ></i>
                <h5 className="mt-3">{jobSearchContactCount}</h5>
                <p className="text-muted mb-3">Employer Contacts Logged</p>
                <button
                  className="btn btn-info"
                  onClick={() => setView("job-search")}
                >
                  <i className="bi bi-file-earmark-text me-2"></i>
                  Manage Job Search Contacts
                </button>
                <p className="text-muted small mt-2 mb-0">
                  Log contacts and generate CRP-33 PDF
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default JobPlacementRelatedForms;